import { Chart } from "@antv/g2";
import * as _ from "@antv/util";
import { useEffect, useRef } from "react";
import { theme } from "antd";
import { isEqual } from "lodash";

export default function CustomLineAreaChart({
  dataLine = [],
  dataArea = [],
  yLine = "value",
  xLine = "time",
  colorLine = "series",
  yArea = "value",
  xArea = "time",
  colorArea = "series",
  xAxisTitleLine = false,
  yAxisTitleLine = false,
  xAxisTitleArea = false,
  yAxisTitleArea = false,
  chartDomainLine = [],
  chartRangeLine = [],
}) {
  const containerRef = useRef(_.uniqueId("lineareachart-"));
  const container = containerRef.current;
  const chartRef = useRef(null);
  const { token } = theme.useToken();

  // Track previous data to avoid unnecessary updates
  const prevDataLineRef = useRef([]);
  const prevDataAreaRef = useRef([]);

  useEffect(() => {
    chartRef.current?.chart?.clear();

    const chartContainer = document.getElementById(container);
    chartContainer.innerHTML = "";

    const chart = new Chart({
      container: container,
      padding: "auto",
      autoFit: true,
    });

    const line = chart
      .line()
      .data(dataLine)
      .encode("x", xLine)
      .encode("y", yLine)
      .encode("color", colorLine)
      .encode("series", "type1") // to show legend per series
      .encode("shape", "smooth")
      .scale("y", { independent: true })
      .scale("color", {
        domain: chartDomainLine,
        range: chartRangeLine,
      })
      .axis("x", {
        title: xAxisTitleLine,
        lineStroke: token?.colorTextBase,
        tickStroke: token?.colorTextBase,
        titleFill: token?.colorTextBase,
        labelFill: token?.colorTextBase,
      })
      .axis("y", {
        grid: null,
        title: yAxisTitleLine,
        lineStroke: token?.colorTextBase,
        tickStroke: token?.colorTextBase,
        titleFill: token?.colorTextBase,
        labelFill: token?.colorTextBase,
      })
      .legend({
        color: {
          itemLabelFill: token.colorTextBase,
        },
      });

    chart.interaction("tooltip", {
      render: (event, { title, items }) => {
        return `<div style="width:150px;">
            <h3 style="padding:0;margin:0">${title}</h3>
            <ul style="padding: 0; margin: 0; list-style: none;">
              ${items
                .map((d) => {
                  // Format the value with commas and 2 decimal places
                  const formattedValue = d.value
                    ? Number(d.value).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : d.value === 0
                      ? Number(d.value).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : d.value;
                  return `
                    <li style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                      <span style="display: inline-flex; align-items: center;">
                        <span style="width: 8px; height: 8px; background-color: ${d.color}; border-radius: 50%; margin-right: 8px;"></span>
                        <span>${d.name}</span>
                      </span>
                      <span>${formattedValue}</span>
                    </li>`;
                })
                .join("")}
            </ul>
          </div>`;
      },
    });

    const area = chart
      .area()
      .data(dataArea)
      .encode("x", xArea)
      .encode("y", yArea)
      .encode("color", colorArea)
      .scale("y", { nice: true })
      .axis("x", {
        title: xAxisTitleArea,
        lineStroke: token?.colorTextBase,
        tickStroke: token?.colorTextBase,
        titleFill: token?.colorTextBase,
        labelFill: token?.colorTextBase,
      })
      .axis("y", {
        title: yAxisTitleArea,
        position: "right",
        titleSpacing: 20,
        lineStroke: token?.colorTextBase,
        tickStroke: token?.colorTextBase,
        titleFill: token?.colorTextBase,
        labelFill: token?.colorTextBase,
      })
      .style("fill", "gray")
      .style("fillOpacity", 0.5);

    chart.render();

    chartRef.current = {
      chart,
      line,
      area,
    };

    return () => {
      chartRef.current?.chart?.destroy();
    };
  }, [token]);

  // Only update chart if data actually changed
  useEffect(() => {
    if (!isEqual(prevDataLineRef.current, dataLine)) {
      chartRef.current.line?.changeData(dataLine);
      prevDataLineRef.current = dataLine;
    }
  }, [dataLine]);

  // Only update chart if data actually changed
  useEffect(() => {
    if (!isEqual(prevDataAreaRef.current, dataArea)) {
      chartRef.current.area?.changeData(dataArea);
      prevDataAreaRef.current = dataArea;
    }
  }, [dataArea]);

  return <div id={container} style={{ height: "100%", width: "100%" }}></div>;
}
